import { useEffect, useState } from 'react'
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import AppLayout from './components/AppLayout'
import Node from './pages/Node'
import Login from './pages/Login'
import { initAnalytics } from './analytics'
import { isTokenValid, ensureAuthenticated } from './services/api'
import { initDeeplink } from './services/deeplink'
import './App.css'

// null = still checking (may need to await a token refresh), true/false = resolved
function ProtectedLayout() {
  const [authorized, setAuthorized] = useState(() => (isTokenValid() ? true : null))

  useEffect(() => {
    if (authorized !== null) return
    ensureAuthenticated().then(setAuthorized)
  }, [authorized])

  if (authorized === null) return null
  if (!authorized) return <Navigate to="/login" replace />
  return <AppLayout />
}

/**
 * Estimate iOS safe-area insets when CSS `env(safe-area-inset-*)` is not
 * honored (Capacitor's WKWebView can report 0px even when laid out edge-to-edge).
 * Returns the portrait status-bar (top) and home-indicator (bottom) heights.
 */
function estimateIosSafeArea() {
  // Landscape — the notch/home indicator sit on the sides, no top inset.
  if (window.innerWidth > window.innerHeight) return { top: 0, bottom: 0 };
  const tall = Math.max(window.screen?.width || 0, window.screen?.height || 0);
  if (tall <= 736) return { top: 20, bottom: 0 }; // home-button iPhones (SE, 8, 8+)
  const dynamicIsland = [840, 852, 874, 932, 940, 956].includes(tall);
  return { top: dynamicIsland ? 59 : 47, bottom: 34 };
}

function App() {
  const isNative = Capacitor.isNativePlatform()
  const Router = isNative ? HashRouter : BrowserRouter
  const routerProps = isNative ? {} : { basename: '/' }

  useEffect(() => {
    if (isNative) initDeeplink();
    initAnalytics();
  }, []);

  // ── Safe-area insets (iOS notch / status bar) ──────────────────
  // The topbar and block list pad with env(safe-area-inset-*) in CSS, but
  // Capacitor's WKWebView can report 0px even when edge-to-edge. Measure the
  // real insets via a probe and, on iOS, fall back to a device estimate so the
  // JulyNote title is never hidden behind the status bar.
  useEffect(() => {
    const root = document.documentElement;

    const applySafeArea = () => {
      const probe = document.createElement('div');
      probe.style.cssText =
        'position:fixed;left:0;top:0;width:1px;height:1px;visibility:hidden;pointer-events:none;' +
        'padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);';
      document.body.appendChild(probe);
      const cs = getComputedStyle(probe);
      let top = parseFloat(cs.paddingTop) || 0;
      let bottom = parseFloat(cs.paddingBottom) || 0;
      probe.remove();

      if (isNative && Capacitor.getPlatform() === 'ios' && top === 0) {
        const est = estimateIosSafeArea();
        top = est.top;
        if (bottom === 0) bottom = est.bottom;
      }

      root.style.setProperty('--safe-top', `${top}px`);
      root.style.setProperty('--safe-bottom', `${bottom}px`);
    };

    applySafeArea();
    window.addEventListener('resize', applySafeArea);
    return () => {
      window.removeEventListener('resize', applySafeArea);
      root.style.removeProperty('--safe-top');
      root.style.removeProperty('--safe-bottom');
    };
  }, [isNative]);

  return (
    <Router {...routerProps}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<ProtectedLayout />}>
          <Route index element={<Node />} />
        </Route>
      </Routes>
    </Router>
  )
}

export default App
